// src/RequireKycApproved.jsx
import React, { useEffect, useRef, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { getToken } from "./globalfunction";
import { serviceUrl } from "./constant";
import { Preloader } from "./components";

const pickPayload = (res) => {
  const d = res?.data?.data ?? res?.data;
  if (Array.isArray(d?.items)) return d.items[0] ?? null;
  if (Array.isArray(d)) return d[0] ?? null;
  if (Array.isArray(res?.data)) return res.data[0] ?? null;
  return d ?? null;
};

const KYC_APPROVED = 1;
const KYC_SUBMITTED = 0;
// const KYC_REJECTED = 2; // treated as /kyc/add as per your rule

export default function RequireKycApproved() {
  const [loading, setLoading] = useState(true);

  // keep your cache
  const [approved, setApproved] = useState(() => {
    const cached = sessionStorage.getItem("kyc_can_access");
    return cached === "true";
  });

  // IMPORTANT: use undefined as "not decided yet"
  // undefined = waiting for backend
  // null = backend says no kyc record
  // 0/1/2 = backend status
  const [kycStatus, setKycStatus] = useState(undefined);

  const location = useLocation();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();

    (async () => {
      try {
        getToken();

        let res;
        try {
          res = await axios.get(`${serviceUrl}investor/kyc/verify`, {
            signal: controller.signal,
          });
        } catch (e) {
          if (e?.response?.status === 404) {
            res = await axios.get(`${serviceUrl}investor/dashboard`, {
              signal: controller.signal,
            });
          } else {
            throw e;
          }
        }

        const payload = pickPayload(res) || {};

        const statusRaw = payload?.kyc?.status; // can be 0/1/2 or null
        const statusResolved =
          typeof statusRaw === "undefined" ? undefined : statusRaw === null ? null : Number(statusRaw);

        console.log("[KYC GUARD] payload:", payload);
        console.log("[KYC GUARD] resolved kyc.status:", statusResolved);

        const canAccess =
          payload?.onboarding?.canAccessGatedRoutes ??
          Number(statusResolved) === KYC_APPROVED;

        if (!mounted.current) return;

        setKycStatus(statusResolved);
        setApproved(Boolean(canAccess));
        sessionStorage.setItem("kyc_can_access", String(Boolean(canAccess)));
      } catch (e) {
        if (!mounted.current) return;

        console.error("[KYC GUARD] error:", {
          status: e?.response?.status,
          data: e?.response?.data,
          message: e?.message,
        });

        if (e?.response?.status === 401) {
          setApproved(false);
          setKycStatus(undefined);
          sessionStorage.removeItem("kyc_can_access");
          setLoading(false);
          return;
        }

        setApproved(false);
        setKycStatus(undefined);
        sessionStorage.removeItem("kyc_can_access");
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();

    return () => {
      mounted.current = false;
      controller.abort();
    };
  }, []);

  // ✅ key fix: if status is NOT resolved yet, do NOT redirect anywhere
  if (loading || (kycStatus === undefined && !approved)) return <Preloader />;

  // ✅ approved => allow
  if (approved) return <Outlet />;

  // ❌ not approved => your rules
  // status=0 => submitted page
  if (kycStatus === KYC_SUBMITTED) {
    console.log("[KYC GUARD] Redirect → /kyc/submitted (status=0)");
    return (
      <Navigate
        to="/kyc/submitted"
        replace
        state={{ from: location, reason: "kyc_submitted" }}
      />
    );
  }

  // status=2 or null or anything else => /kyc/add
  console.log("[KYC GUARD] Redirect → /kyc/add (status=null/2/other)");
  return (
    <Navigate
      to="/kyc/add"
      replace
      state={{ from: location, reason: "kyc" }}
    />
  );
}
